/*
 * Sinderella Loot Cart: free-gift ladder engine for the Minimog cart drawer and cart page.
 * Source of truth: theme/sinderella-cart/ in the sinderella-engage repo. Config comes from the
 * shop metafield sinderella.gift_ladder, rendered into <script data-sd-loot-cfg> by sd-loot-card.liquid.
 *
 * Contract:
 * - Gift lines carry the line property `_sd_gift = <tier key>`; they are free through Shopify
 *   automatic Buy X Get Y discounts ("N free gifts on ₹T+", any of the gift products). Gifts never
 *   count towards any tier. Qualifying total = paid lines, excluding gift products.
 *   LOOT DEAL: the gift discount is a Basic 100% off Khwaab + Vaada with a ₹149 minimum, not a ₹1,750
 *   BXGY, so THIS engine is the only ₹1,750 gate: when it fails or lags, gifts go out free, not charged.
 * - Leftover lines from the old gift sync (property `_free_gift`, retired 2026-09-20) still sit in
 *   some returning carts. They are removed on the first sync and never count towards a tier.
 * - A gift line that is still charged after a sync is removed and its tier blocked until the
 *   qualifying total changes.
 * - Gift writes are sent through a hidden same-origin frame's fetch so Shopify's customer-event
 *   runtime (and app pixels such as Aimerce -> Meta) never reports a free gift as an add to cart.
 * - Checkout clicks wait (max 3 s) until no sync is pending, then re-click the live button, so GoKwik
 *   reads a settled /cart.js.
 * - Minimog quantity/remove controls target lines by key (data-sd-key), so gift lines added at the
 *   top of the cart can never shift which line a shopper's tap changes.
 */
(function (root) {
  'use strict';

  var Core = (function () {
    function giftKey(item) {
      var p = item && item.properties;
      return p && p._sd_gift ? String(p._sd_gift) : '';
    }

    function legacyGift(item) {
      var p = item && item.properties;
      return !!(p && p._free_gift && !p._sd_gift);
    }

    // Loot Deal strict mode: Shopify leaves any unit that already got a product discount out of the
    // gift minimum, and a line holding the free unit may be left out whole. Strict counts such a
    // line as 0 (never over-promises); 'paid' counts what the line costs.
    function lineDisc(it) {
      if (it && it.line_level_total_discount != null) return Number(it.line_level_total_discount) || 0;
      var s = 0;
      ((it && it.line_level_discount_allocations) || []).forEach(function (a) { s += Number((a && a.amount) || 0); });
      return s;
    }

    function strictMode(cfg) {
      return !!dealCfg(cfg) && (cfg.deal.q_mode || 'strict') === 'strict';
    }

    function qualifying(cart, cfg) {
      var ids = (cfg && cfg.gift_product_ids) || [];
      var strict = strictMode(cfg);
      var q = 0;
      ((cart && cart.items) || []).forEach(function (it) {
        if (giftKey(it) || legacyGift(it)) return;
        if (ids.indexOf(String(it.product_id)) !== -1) return;
        if (strict && lineDisc(it) > 0) return;
        q += Number(it.final_line_price || 0);
      });
      return q - codeCut(cart, cfg, q);
    }

    // A code never stacks with Buy 2 get 1: Shopify keeps whichever saves more (a code that does not
    // combine with product discounts is weighed against the deal + the gift discount). Counted at full
    // price while the code wins, the gift pair would flip: no gifts -> the code wins -> q is full price ->
    // add; gifts in -> the deal wins -> q drops -> remove, on every sync. So while a code has displaced the
    // deal, q counts what the shopper would pay with the deal on, never more: the k lowest-priced units. Strict
    // mode cuts only lines it counted: the k lowest-priced of those, or with a multi-unit line (which line
    // Shopify frees is unknown) every counted deal line that could hold a FREE unit. Codes still never
    // lower q by their own value.
    function codeCut(cart, cfg, q) {
      var d = dealCfg(cfg);
      if (!d || q <= 0 || !codeApplied(cart).code) return 0;
      var u = dealUnits(cart, cfg);
      var k = freeCount(u.length, d);
      // An automatic allocation under another title (drift) means the deal is on under a new name, not
      // displaced: a code that pushed it out would have pushed out every product discount.
      var sums = dealSums(cart, cfg);
      if (k === 0 || sums.alloc > 0 || sums.drift > 0) return 0;
      if (!strictMode(cfg)) return Math.min(q, cheapestSum(u, k));
      var counted = ((cart && cart.items) || []).filter(function (it) { return dealLine(it, cfg) && lineDisc(it) <= 0; });
      var cut = 0;
      if (multiQty(cart, cfg)) {
        counted.forEach(function (it) { if (unitPrice(it) <= u[k - 1]) cut += Number(it.final_line_price || 0); });
      } else {
        cut = cheapestSum(counted.map(unitPrice).sort(byNum), k);
      }
      return Math.min(q, cut);
    }

    function normSize(title) {
      var t = String(title || '').toUpperCase().trim();
      if (/^(XS|S|SMALL)\b/.test(t)) return 'S';
      if (/^(M|MEDIUM)\b/.test(t)) return 'M';
      if (/^(L|LARGE|XL|XXL|2XL|3XL|4XL|5XL)\b/.test(t)) return 'L';
      return '';
    }

    function inferSize(cart, cfg) {
      var counts = { S: 0, M: 0, L: 0 };
      var ids = (cfg && cfg.gift_product_ids) || [];
      ((cart && cart.items) || []).forEach(function (it) {
        if (giftKey(it) || legacyGift(it) || ids.indexOf(String(it.product_id)) !== -1) return;
        (it.options_with_values || []).forEach(function (o) {
          if (!o || !/size/i.test(o.name || '')) return;
          var s = normSize(o.value);
          if (s) counts[s] += Number(it.quantity || 1);
        });
      });
      var best = '';
      ['M', 'S', 'L'].forEach(function (s) {
        if (counts[s] > 0 && (!best || counts[s] > counts[best])) best = s;
      });
      return best;
    }

    function pickVariant(tier, prefs, cart, cfg) {
      var avail = (tier.variants || []).filter(function (v) { return v && v.available; });
      if (!avail.length) return null;
      if (!tier.size_pick) return avail[0];
      var want = prefs && prefs.size && prefs.size[tier.key];
      var i;
      if (want) {
        for (i = 0; i < avail.length; i++) if (String(avail[i].id) === String(want)) return avail[i];
      }
      var s = inferSize(cart, cfg) || 'M';
      for (i = 0; i < avail.length; i++) if (normSize(avail[i].title) === s) return avail[i];
      for (i = 0; i < avail.length; i++) if (normSize(avail[i].title) === 'M') return avail[i];
      return avail[0];
    }

    // A shopper who already has a paid unit of a gift product (bought before it was unlisted, or by
    // direct link) gets that unit free from the same discount, so no separate gift line is added.
    function paidGift(cart, tier) {
      var ids = (tier.variants || []).map(function (v) { return String(v.id); });
      return ((cart && cart.items) || []).some(function (it) {
        return !giftKey(it) && !legacyGift(it) && ids.indexOf(String(it.variant_id)) !== -1;
      });
    }

    function plan(cart, cfg, prefs) {
      prefs = prefs || {};
      var removed = prefs.removed || {};
      var blocked = prefs.blocked || {};
      var q = qualifying(cart, cfg);
      var target = {};
      if (cfg && cfg.enabled) {
        (cfg.tiers || []).forEach(function (t) {
          if (q < Number(t.threshold)) return;
          if (removed[t.key] || blocked[t.key]) return;
          if (paidGift(cart, t)) return;
          var v = pickVariant(t, prefs, cart, cfg);
          if (v) target[t.key] = String(v.id);
        });
      }
      var seen = {};
      var remove = [];
      var fixQty = [];
      var keep = [];
      ((cart && cart.items) || []).forEach(function (it) {
        var key = giftKey(it);
        if (!key) {
          if (legacyGift(it)) remove.push(it);
          return;
        }
        var want = target[key];
        if (!want || String(it.variant_id) !== want || seen[key]) {
          remove.push(it);
          return;
        }
        seen[key] = true;
        keep.push(it);
        if (Number(it.quantity) !== 1) fixQty.push(it);
      });
      var add = [];
      Object.keys(target).forEach(function (k) {
        if (!seen[k]) add.push({ id: Number(target[k]), quantity: 1, properties: { _sd_gift: k } });
      });
      var priced = keep.filter(function (it) {
        return Number(it.quantity) === 1 && Number(it.final_line_price) > 0;
      });
      return { q: q, target: target, add: add, remove: remove, fixQty: fixQty, keep: keep, priced: priced };
    }

    // ---- Loot Deal (Buy 2, get 1 FREE: the lowest-priced eligible unit, max_free per order) ----
    // Fail-closed: anything but the exact approved shape means no deal, and the cart renders as v2.
    function dealCfg(cfg) {
      var d = cfg && cfg.deal;
      if (!d || d.enabled !== true) return null;
      var mf = Number(d.max_free);
      if (Number(d.buy) !== 2 || Number(d.get) !== 1 || (mf !== 1 && mf !== 2)) return null;
      if (!d.title || !String(d.title).trim()) return null;
      return d;
    }

    function dealExcluded(it, cfg) {
      var pid = String(it && it.product_id);
      var ids = ((cfg && cfg.gift_product_ids) || []).map(String);
      var ex = ((cfg && cfg.deal && cfg.deal.exclude_ids) || []).map(String);
      return ids.indexOf(pid) !== -1 || ex.indexOf(pid) !== -1;
    }

    function unitPrice(it) {
      return Number(it.original_price != null ? it.original_price : it.price || 0);
    }

    // A paid line that takes part in the deal (same filter as the Liquid card).
    function dealLine(it, cfg) {
      return !giftKey(it) && !legacyGift(it) && !dealExcluded(it, cfg) && unitPrice(it) > 0;
    }

    function byNum(a, b) { return a - b; }

    function dealUnits(cart, cfg) {
      var out = [];
      ((cart && cart.items) || []).forEach(function (it) {
        if (!dealLine(it, cfg)) return;
        var u = unitPrice(it);
        var qn = Math.min(Number(it.quantity || 0), 99);
        for (var i = 0; i < qn; i++) out.push(u);
      });
      return out.sort(byNum);
    }

    function freeCount(n, d) {
      var per = Number(d.buy) + Number(d.get);
      return Math.min(Number(d.max_free), Math.floor(n / per) * Number(d.get));
    }

    function cheapestSum(units, k) {
      var s = 0;
      for (var i = 0; i < k && i < units.length; i++) s += units[i];
      return s;
    }

    function dealSums(cart, cfg) {
      var d = dealCfg(cfg);
      var r = { alloc: 0, drift: 0 };
      if (!d) return r;
      ((cart && cart.items) || []).forEach(function (it) {
        if (!dealLine(it, cfg)) return;
        (it.line_level_discount_allocations || []).forEach(function (a) {
          var app = a && a.discount_application;
          if (!app || app.type === 'discount_code') return;
          if (app.title === d.title) r.alloc += Number(a.amount || 0);
          else r.drift += Number(a.amount || 0);
        });
      });
      return r;
    }

    // The Buy 2 get 1 amount Shopify actually gave: automatic allocations with the exact title.
    function dealAllocated(cart, cfg) { return dealSums(cart, cfg).alloc; }

    // Automatic allocations on deal lines under any other title (a renamed discount).
    function dealTitleDrift(cart, cfg) { return dealSums(cart, cfg).drift; }

    // List price of gift lines that are actually free. A still-charged gift is never counted.
    function giftWorth(cart) {
      var s = 0;
      ((cart && cart.items) || []).forEach(function (it) {
        if (giftKey(it) && Number(it.final_line_price) === 0) s += Number(it.original_line_price || 0);
      });
      return s;
    }

    // Only codes Shopify applied; a code listed with applicable:false has no allocation. An order code
    // sits in cart_level_discount_applications, a product code in the lines' allocations.
    function codeApplied(cart) {
      var v = 0;
      ((cart && cart.cart_level_discount_applications) || []).forEach(function (a) {
        if (a && a.type === 'discount_code') v += Number(a.total_allocated_amount || 0);
      });
      ((cart && cart.items) || []).forEach(function (it) {
        ((it && it.line_level_discount_allocations) || []).forEach(function (a) {
          var app = a && a.discount_application;
          if (app && app.type === 'discount_code') v += Number(a.amount || 0);
        });
      });
      return { code: v > 0, value: v };
    }

    function multiQty(cart, cfg) {
      return ((cart && cart.items) || []).some(function (it) {
        return dealLine(it, cfg) && Number(it.quantity || 0) >= 2;
      });
    }

    function dealState(cart, cfg) {
      var gifts = giftWorth(cart);
      var d = dealCfg(cfg);
      if (!d) {
        return { n: 0, k: 0, k1: 0, toNext: 0, maxed: false, m1: 0, m2: 0, expected: 0, actual: 0, missing: false, drift: 0, code: false, codeValue: 0, gifts: gifts, looted: gifts };
      }
      var u = dealUnits(cart, cfg);
      var n = u.length;
      var k = freeCount(n, d);
      var per = Number(d.buy) + Number(d.get);
      var maxed = k >= Number(d.max_free);
      var sums = dealSums(cart, cfg);
      var c = codeApplied(cart);
      return {
        n: n,
        k: k,
        k1: freeCount(n + 1, d),
        toNext: maxed ? 0 : per * (Math.floor(n / per) + 1) - n,
        maxed: maxed,
        m1: u[0] || 0,
        m2: u[1] || 0,
        expected: cheapestSum(u, k),
        actual: sums.alloc,
        missing: k > 0 && sums.alloc === 0,
        drift: sums.drift,
        code: c.code,
        codeValue: c.value,
        gifts: gifts,
        looted: sums.alloc + (cfg.show_gift_worth === false ? 0 : gifts)
      };
    }

    // The deal is due but Shopify did not apply it, and no code explains that. With a code on, qualifying
    // already counts the deal as applied (codeCut), so the deal formulas below hold.
    function dealOff(cart, cfg) {
      var st = dealState(cart, cfg);
      return st.missing && !st.code;
    }

    // A product code (line-level allocation) displaced the deal: whether it also discounts the next piece
    // is unknown, so the qualifying total after an add is unknown too.
    function codeBlind(cart, cfg) {
      if (!dealState(cart, cfg).missing) return false;
      return ((cart && cart.items) || []).some(function (it) {
        return ((it && it.line_level_discount_allocations) || []).some(function (a) {
          return !!(a && a.discount_application && a.discount_application.type === 'discount_code' && Number(a.amount) > 0);
        });
      });
    }

    // Qualifying total after adding one unit priced P. null = cannot be known (strict mode with a
    // multi-unit line, where Shopify may drop a whole line from the gift minimum; or a product code that
    // displaced the deal).
    function qAfterAdd(cart, cfg, price, eligible) {
      var q = qualifying(cart, cfg);
      var P = Number(price || 0);
      var d = dealCfg(cfg);
      if (!d || eligible === false || dealOff(cart, cfg)) return q + P;
      if ((strictMode(cfg) && multiQty(cart, cfg)) || codeBlind(cart, cfg)) return null;
      var u = dealUnits(cart, cfg);
      var u2 = u.concat([P]).sort(byNum);
      return q + P - (cheapestSum(u2, freeCount(u2.length, d)) - cheapestSum(u, freeCount(u.length, d)));
    }

    // Lowest-priced single eligible piece that lifts the qualifying total to T. 0 = already there,
    // 1 = any piece, null = unknown (see qAfterAdd).
    function needFor(cart, cfg, T) {
      var q = qualifying(cart, cfg);
      if (q >= T) return 0;
      var d = dealCfg(cfg);
      if (!d || dealOff(cart, cfg)) return T - q;
      if ((strictMode(cfg) && multiQty(cart, cfg)) || codeBlind(cart, cfg)) return null;
      var u = dealUnits(cart, cfg);
      var k = freeCount(u.length, d);
      var k1 = freeCount(u.length + 1, d);
      if (k1 === 0) return T - q;
      var F = cheapestSum(u, k);
      var C1 = q + F - cheapestSum(u, k1 - 1);
      if (C1 >= T) return 1;
      return T - q - F + cheapestSum(u, k1);
    }

    function groups(cfg) {
      var m = {};
      var out = [];
      ((cfg && cfg.tiers) || []).forEach(function (t) {
        var k = Number(t.threshold);
        if (!m[k]) { m[k] = { threshold: k, keys: [] }; out.push(m[k]); }
        m[k].keys.push(t.key);
      });
      return out.sort(function (a, b) { return a.threshold - b.threshold; });
    }

    // Session prefs from an older config are dropped once a v3+ config appears (and on any change
    // after that), so a gift removed under the old ladder is offered again.
    function shouldReset(stored, cur) {
      return stored !== cur && (stored != null || cur >= 3);
    }

    return {
      plan: plan, qualifying: qualifying, inferSize: inferSize, pickVariant: pickVariant, giftKey: giftKey, legacyGift: legacyGift, normSize: normSize,
      lineDisc: lineDisc, dealCfg: dealCfg, dealExcluded: dealExcluded, dealUnits: dealUnits, freeCount: freeCount, cheapestSum: cheapestSum,
      dealAllocated: dealAllocated, dealTitleDrift: dealTitleDrift, giftWorth: giftWorth, codeApplied: codeApplied, multiQty: multiQty,
      dealState: dealState, qAfterAdd: qAfterAdd, needFor: needFor, groups: groups, shouldReset: shouldReset
    };
  })();

  root.SDLootCore = Core;
  if (typeof module !== 'undefined' && module.exports) module.exports = Core;
  if (typeof document === 'undefined' || root.SDLoot) return;

  var doc = document;
  var KEY = { removed: 'sd_loot_removed', blocked: 'sd_loot_blocked', size: 'sd_loot_size', celebrated: 'sd_loot_celebrated', fill: 'sd_loot_fill', ver: 'sd_loot_ver', deal: 'sd_loot_deal' };
  var mem = {};
  var ss = {
    get: function (k, d) {
      try { var v = sessionStorage.getItem(k); if (v) return JSON.parse(v); } catch (e) {}
      return Object.prototype.hasOwnProperty.call(mem, k) ? mem[k] : d;
    },
    set: function (k, v) {
      mem[k] = v;
      try { sessionStorage.setItem(k, JSON.stringify(v)); } catch (e) {}
    }
  };
  var S = { busy: null, again: false, timer: null, baselined: false, holding: false, acted: false };

  function cfg() {
    var el = doc.querySelector('script[data-sd-loot-cfg]');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }
  function prefs() {
    return { removed: ss.get(KEY.removed, {}), blocked: ss.get(KEY.blocked, {}), size: ss.get(KEY.size, {}) };
  }
  function track(el) {
    try { if (typeof root.sv7Track === 'function') root.sv7Track('ElementClick', { el: String(el).slice(0, 100), page: location.pathname }); } catch (e) {}
  }
  function sections() {
    var list = [];
    try {
      doc.documentElement.dispatchEvent(new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: list } }));
    } catch (e) {}
    return list.filter(function (s, i) { return s && list.indexOf(s) === i; }).slice(0, 5);
  }
  function routes() {
    var r = (root.MinimogSettings && root.MinimogSettings.routes) || {};
    var base = (root.Shopify && root.Shopify.routes && root.Shopify.routes.root) || '/';
    return {
      add: (r.cart_add_url || base + 'cart/add') + '.js',
      update: (r.cart_update_url || base + 'cart/update') + '.js',
      change: (r.cart_change_url || base + 'cart/change') + '.js',
      cart: (r.cart_url || base + 'cart') + '.js'
    };
  }
  function timed(opts, Ctor) {
    var AC = Ctor || (typeof AbortController === 'function' ? AbortController : null);
    var ac = AC ? new AC() : null;
    if (ac) { setTimeout(function () { try { ac.abort(); } catch (e) {} }, 8000); opts.signal = ac.signal; }
    return opts;
  }

  // Gift writes are not shopper cart actions. Shopify's customer-event runtime reports every cart
  // write made through the page's fetch as product_added_to_cart / product_removed_from_cart, and
  // app pixels forward them (Aimerce sends them to Meta as AddToCart at the gift's list price). A
  // hidden same-origin frame has its own untouched fetch, so gift writes go through it. Shopper adds
  // from the drawer (v2 gap-closer and upsell shelf) keep the page fetch and stay tracked.
  var quiet = null;
  function quietFetch() {
    if (quiet && quiet.frame.isConnected) return quiet;
    quiet = null;
    try {
      var f = doc.createElement('iframe');
      f.hidden = true;
      f.tabIndex = -1;
      f.setAttribute('aria-hidden', 'true');
      f.setAttribute('title', 'cart');
      (doc.body || doc.documentElement).appendChild(f);
      var w = f.contentWindow;
      if (w && typeof w.fetch === 'function') {
        quiet = { frame: f, fetch: w.fetch.bind(w), AC: typeof w.AbortController === 'function' ? w.AbortController : null };
      }
    } catch (e) {}
    return quiet;
  }
  function post(url, body, gift) {
    var abs = /^\/(?!\/)/.test(url) ? location.origin + url : url;
    var build = function (Ctor) {
      return timed({
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(body)
      }, Ctor);
    };
    var qf = gift ? quietFetch() : null;
    var req;
    if (qf) {
      try {
        req = qf.fetch(abs, build(qf.AC)).catch(function (err) {
          if (qf.frame.isConnected) throw err;
          quiet = null;
          return fetch(abs, build());
        });
      } catch (e) {
        quiet = null;
        req = fetch(abs, build());
      }
    } else {
      req = fetch(abs, build());
    }
    return req.then(function (r) { return r.json(); });
  }
  function getCart() {
    return fetch(routes().cart, timed({ credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } }))
      .then(function (r) { return r.json(); });
  }
  function repaint(resp, tagged) {
    if (!resp || !resp.sections) return;
    if (tagged) resp.sdLoot = true;
    try {
      if (root.MinimogEvents && root.MinimogTheme && root.MinimogTheme.pubSubEvents) {
        root.MinimogEvents.emit(root.MinimogTheme.pubSubEvents.cartUpdate, { cart: resp });
      }
    } catch (e) {}
  }
  // A block remembers the qualifying total it was set at. Once the bag changes the tier is tried
  // again, so one bad moment (a sold-out variant, a discount that briefly did not apply) never costs
  // the shopper that gift for the rest of the session.
  function block(keys, why, q) {
    var b = ss.get(KEY.blocked, {});
    keys.forEach(function (k) { b[k] = { why: why || 'blocked', q: q }; });
    ss.set(KEY.blocked, b);
  }
  function pruneBlocks(q) {
    var b = ss.get(KEY.blocked, {});
    var changed = false;
    Object.keys(b).forEach(function (k) {
      var v = b[k];
      if (!v || typeof v !== 'object' || v.q !== q) { delete b[k]; changed = true; }
    });
    if (changed) ss.set(KEY.blocked, b);
  }

  async function addGifts(items, q) {
    var sec = sections();
    var resp = await post(routes().add, { items: items, sections: sec, sections_url: location.pathname }, true);
    if (resp && !resp.status) return resp;
    var last = null;
    for (var i = 0; i < items.length; i++) {
      var one = await post(routes().add, { items: [items[i]], sections: sec, sections_url: location.pathname }, true);
      if (one && one.status) {
        block([items[i].properties._sd_gift], 'add-failed', q);
        track('cart:gift-add-failed-' + items[i].properties._sd_gift);
      } else {
        last = one;
      }
    }
    return last;
  }

  // One step towards the target cart. Removals and quantity fixes go first; adds next; only when the
  // cart already matches the target is a still-charged gift judged a real mismatch. While extra gift
  // lines are still present Shopify may give the free slot to the line about to leave, so judging
  // earlier would block a gift the shopper has earned.
  async function writeRound(c, allowAdds) {
    var cart = await getCart();
    pruneBlocks(Core.qualifying(cart, c));
    var p = Core.plan(cart, c, prefs());
    var updates = {};
    var n = 0;
    p.remove.forEach(function (it) { updates[it.key] = 0; n++; });
    p.fixQty.forEach(function (it) { if (updates[it.key] === undefined) { updates[it.key] = 1; n++; } });
    if (n) {
      var r1 = await post(routes().update, { updates: updates, sections: sections(), sections_url: location.pathname }, true);
      if (r1 && !r1.status) repaint(r1, true);
      return 'changed';
    }
    if (allowAdds && p.add.length) {
      var r2 = await addGifts(p.add, p.q);
      if (r2) repaint(r2, true);
      return 'added';
    }
    if (p.priced.length) {
      block(p.priced.map(Core.giftKey), 'charged', p.q);
      var up = {};
      p.priced.forEach(function (it) { up[it.key] = 0; track('cart:gift-mismatch-' + Core.giftKey(it)); });
      var r3 = await post(routes().update, { updates: up, sections: sections(), sections_url: location.pathname }, true);
      if (r3 && !r3.status) repaint(r3, true);
      return 'changed';
    }
    return 'settled';
  }

  async function runSync() {
    var c = cfg();
    if (!c) return;
    for (var round = 0; round < 5; round++) {
      if (await writeRound(c, true) === 'settled') return;
    }
    for (var extra = 0; extra < 2; extra++) {
      if (await writeRound(c, false) === 'settled') return;
    }
  }

  function sync() {
    if (S.busy) { S.again = true; return S.busy; }
    // S.busy must never reject: the checkout hold waits on it, and a rejection would leave the hold stuck.
    S.busy = runSync().catch(function () {}).then(function () {
      S.busy = null;
      if (S.again) { S.again = false; schedule(0); } else { try { fixPending(); } catch (e) {} }
    });
    return S.busy;
  }
  function schedule(ms) {
    clearTimeout(S.timer);
    S.timer = setTimeout(function () { S.timer = null; sync(); }, ms == null ? 60 : ms);
  }
  function pending() { return !!(S.busy || S.timer || S.again); }
  function wait(ms) { return new Promise(function (res) { setTimeout(res, Math.max(0, ms)); }); }
  function settle(deadline) {
    if (!pending() || Date.now() >= deadline) return Promise.resolve();
    return Promise.race([S.busy || wait(80), wait(deadline - Date.now())]).then(function () { return settle(deadline); });
  }

  function state() {
    var el = doc.querySelector('script[data-sd-loot-state]');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  function animateFill() {
    var fill = doc.querySelector('[data-sd-fill]');
    if (!fill) return;
    var now = parseFloat(fill.getAttribute('data-sd-fill')) || 0;
    var prev = ss.get(KEY.fill, null);
    ss.set(KEY.fill, now);
    if (prev == null || prev === now) return;
    fill.style.transition = 'none';
    fill.style.width = prev + '%';
    void fill.offsetWidth;
    fill.style.transition = '';
    fill.style.width = now + '%';
  }

  function markTiles() {
    var removed = ss.get(KEY.removed, {});
    var blocked = ss.get(KEY.blocked, {});
    Object.keys(removed).concat(Object.keys(blocked)).forEach(function (k) {
      var tile = doc.querySelector('[data-sd-tile="' + k + '"].is-pending');
      if (!tile) return;
      tile.classList.remove('is-pending');
      tile.classList.add('is-locked');
      var chip = tile.querySelector('.sd-chip');
      if (chip) chip.textContent = removed[k] ? 'Removed' : 'Unavailable';
    });
  }

  function renderRemoved() {
    var box = doc.querySelector('[data-sd-gifts]');
    var st = state();
    if (!box || !st) return;
    var removed = ss.get(KEY.removed, {});
    (st.tiers || []).forEach(function (t) {
      if (!removed[t.key] || !t.qualifies) return;
      if (box.querySelector('[data-sd-addback="' + t.key + '"]')) return;
      var row = doc.createElement('div');
      row.className = 'sd-grow sd-grow--removed';
      row.innerHTML = '<span class="sd-grow__t"></span><button type="button" class="sd-link" data-sd-addback="' + t.key + '" data-sv7-el="cart:gift-addback">Add back</button>';
      row.querySelector('.sd-grow__t').textContent = st.deal ? (t.name || t.label) + ' removed' : 'FREE ' + t.label + ' removed';
      box.appendChild(row);
      box.hidden = false;
    });
  }

  function hideDupes() {
    var gap = doc.querySelector('[data-sd-gap-pid]');
    var pid = gap && gap.getAttribute('data-sd-gap-pid');
    if (!pid) return;
    var dup = doc.querySelector('[data-sd-up-pid="' + pid + '"]');
    if (dup) dup.remove();
  }

  // ---- Loot Deal runtime. Liquid prints the true numbers; this only animates and reports. ----
  var fired = {};
  function trackOnce(el) {
    if (fired[el]) return;
    fired[el] = 1;
    track(el);
  }
  function inr(paise) {
    var v = Number(paise || 0) / 100;
    var d = v % 1 === 0 ? 0 : 2;
    try { return '\u20b9' + v.toLocaleString('en-IN', { minimumFractionDigits: d, maximumFractionDigits: d }); } catch (e) { return '\u20b9' + v.toFixed(d); }
  }
  function calm() {
    try { return !!(root.matchMedia && root.matchMedia('(prefers-reduced-motion: reduce)').matches); } catch (e) { return false; }
  }
  // Animates the badge number from the previous value to the one Liquid printed. The Liquid text is
  // always restored at the end, and a timer restores it even when frames are paused (hidden tab).
  function countUp(from, to) {
    var el = doc.querySelector('[data-sd-looted]');
    if (!el) return;
    var truth = el.textContent;
    var done = false;
    var t0 = Date.now();
    var finish = function () {
      if (done) return;
      done = true;
      if (el.isConnected) el.textContent = truth;
    };
    var tick = function () {
      if (done || !el.isConnected) return;
      var p = Math.min(1, (Date.now() - t0) / 400);
      if (p >= 1) { finish(); return; }
      el.textContent = inr(Math.round((from + (to - from) * p) / 100) * 100);
      if (root.requestAnimationFrame) root.requestAnimationFrame(tick); else setTimeout(tick, 16);
    };
    el.textContent = inr(from);
    tick();
    setTimeout(finish, 450);
  }

  function dealPaint(st) {
    var d = st.deal;
    var said = [];
    if (d.missing && !d.code) trackOnce('cart:deal-missing');
    if (Number(d.drift) > 0) trackOnce('cart:deal-title-drift');
    if (d.code && d.missing) trackOnce('cart:deal-code-kept');
    var k = Number(d.k || 0);
    var looted = Number(d.looted || 0);
    var prev = ss.get(KEY.deal, null);
    ss.set(KEY.deal, { k: k, looted: looted });
    if (!prev || typeof prev !== 'object') return said;
    var pk = Number(prev.k || 0);
    var pl = Number(prev.looted || 0);
    if (k > pk) {
      for (var i = pk + 1; i <= k; i++) {
        var slot = doc.querySelector('[data-sd-slot="' + i * 3 + '"]');
        if (slot) slot.classList.add('sd-stamp');
      }
      track((S.acted ? 'cart:deal-free-' : 'cart:deal-restore-') + k);
      said.push(k - pk === 1 ? '1 item FREE with the Loot Deal.' : (k - pk) + ' items FREE with the Loot Deal.');
    }
    if (S.acted && looted > pl) {
      if (!calm()) countUp(pl, looted);
      said.push('Loot Deal: you looted ' + inr(looted) + ' on this order.');
    }
    var live = doc.querySelector('[data-sd-live]');
    if (live && said.length) live.textContent = said.join(' ');
    return said;
  }

  // Liquid shows the gift vault as "landing now" whenever a gift at the step has no free line. Once
  // the engine has settled, a gift that is still missing was removed by the shopper or blocked, so
  // that line would no longer be true.
  function fixPending() {
    var card = doc.querySelector('[data-sd-card][data-sd-pending]');
    if (!card || pending()) return;
    var st = state();
    if (!st || !st.deal) return;
    var removed = ss.get(KEY.removed, {});
    var blocked = ss.get(KEY.blocked, {});
    var why = '';
    var gone = 0;
    (st.tiers || []).forEach(function (t) {
      if (!t.qualifies) return;
      var b = blocked[t.key];
      if (removed[t.key]) { why = 'removed'; gone++; }
      else if (b && typeof b === 'object' && b.q === st.q && !why) why = 'blocked';
    });
    if (!why) return;
    card.removeAttribute('data-sd-pending');
    var set = function (sel, txt) { var el = card.querySelector(sel); if (el) el.textContent = txt; };
    var many = gone > 1;
    set('[data-sd-h1]', why === 'removed' ? (many ? 'Gifts removed \u2713' : 'Gift removed \u2713') : 'Gift unavailable right now');
    set('[data-sd-h2] span', why === 'removed' ? (many ? 'Add them back below' : 'Add it back below') : 'Your items are unchanged');
    set('[data-sd-sub]', '');
    set('[data-sd-vline]', why === 'removed' ? 'Removed' : 'Unavailable');
  }

  function resetOnVersion() {
    var c = cfg();
    var cur = Number((c && c.version) || 1);
    if (Core.shouldReset(ss.get(KEY.ver, null), cur)) {
      ss.set(KEY.removed, {});
      ss.set(KEY.blocked, {});
      ss.set(KEY.size, {});
      ss.set(KEY.celebrated, null);
      ss.set(KEY.fill, null);
      ss.set(KEY.deal, null);
    }
    ss.set(KEY.ver, cur);
  }

  function afterPaint() {
    hideDupes();
    animateFill();
    markTiles();
    renderRemoved();
    var st = state();
    if (!st) return;
    // Deal paint is cosmetic: a fault there must never skip the sync that follows afterPaint().
    var said = [];
    if (st.deal) { try { said = dealPaint(st) || []; } catch (e) { said = []; } }
    try { fixPending(); } catch (e) {}
    var unlocked = st.unlocked || [];
    var done = ss.get(KEY.celebrated, null);
    if (!S.baselined || done === null) {
      S.baselined = true;
      if (done === null) { ss.set(KEY.celebrated, unlocked); return; }
    }
    var fresh = unlocked.filter(function (k) { return done.indexOf(k) === -1; });
    ss.set(KEY.celebrated, done.concat(fresh));
    if (!fresh.length) return;
    var names = [];
    fresh.forEach(function (k, i) {
      var tile = doc.querySelector('[data-sd-tile="' + k + '"]');
      if (tile) {
        tile.style.setProperty('--sd-i', i);
        tile.classList.add('sd-pop');
      }
      (st.tiers || []).forEach(function (t) {
        if (t.key === k) names.push(t.label);
      });
      track((S.acted ? 'cart:gift-unlock-' : 'cart:gift-restore-') + k);
    });
    var live = doc.querySelector('[data-sd-live]');
    if (live && names.length) live.textContent = said.concat(['FREE ' + names.join(' and ') + ' added to your bag.']).join(' ');
  }

  async function addItem(btn) {
    var vid = btn.getAttribute('data-sd-add');
    if (!vid || btn.getAttribute('aria-busy') === 'true') return;
    btn.setAttribute('aria-busy', 'true');
    S.acted = true;
    try {
      var resp = await post(routes().add, { items: [{ id: Number(vid), quantity: 1 }], sections: sections(), sections_url: location.pathname });
      if (resp && resp.status) {
        btn.textContent = 'Sold out';
        return;
      }
      repaint(resp, false);
      try { doc.dispatchEvent(new CustomEvent('product-ajax:added', { detail: { product: resp } })); } catch (e) {}
    } catch (e) {
    } finally {
      btn.removeAttribute('aria-busy');
    }
  }

  async function removeGift(btn) {
    var key = btn.getAttribute('data-sd-gift-remove');
    var line = btn.getAttribute('data-line-key');
    var removed = ss.get(KEY.removed, {});
    removed[key] = 1;
    ss.set(KEY.removed, removed);
    var upd = {};
    upd[line] = 0;
    try {
      var resp = await post(routes().update, { updates: upd, sections: sections(), sections_url: location.pathname }, true);
      repaint(resp, true);
    } catch (e) {
      schedule(0);
    }
  }

  function addBack(btn) {
    var key = btn.getAttribute('data-sd-addback');
    var removed = ss.get(KEY.removed, {});
    delete removed[key];
    ss.set(KEY.removed, removed);
    S.acted = true;
    var row = btn.closest('.sd-grow');
    if (row) row.remove();
    schedule(0);
  }

  function pickSize(btn) {
    if (btn.classList.contains('is-on')) return;
    var key = btn.getAttribute('data-sd-size');
    var vid = btn.getAttribute('data-variant');
    var size = ss.get(KEY.size, {});
    size[key] = vid;
    ss.set(KEY.size, size);
    btn.setAttribute('aria-busy', 'true');
    schedule(0);
  }

  function patchLineKeys() {
    var C = root.customElements && root.customElements.get('m-cart-items');
    if (!C || C.prototype.__sdKeys) return;
    var orig = C.prototype.updateQuantity;
    C.prototype.updateQuantity = function (line, quantity, name, target) {
      var key = null;
      try {
        var node = target && target.getAttribute && target.getAttribute('data-sd-key') ? target : this.querySelector('[data-sd-key][data-index="' + line + '"]');
        key = node && node.getAttribute('data-sd-key');
      } catch (e) {}
      if (!key || typeof root.fetchConfig !== 'function' || !root.MinimogSettings) return orig.apply(this, arguments);
      var self = this;
      try { if (self.loading) self.loading.start(); } catch (e) {}
      var body = JSON.stringify({ id: key, quantity: Number(quantity), sections: sections() });
      fetch(routes().change, Object.assign({}, root.fetchConfig(), { body: body }))
        .then(function (r) { return r.json(); })
        .then(function (response) {
          if (response.errors) {
            try { if (self.loading) self.loading.finish(); } catch (e) {}
            self.onCartError(response.errors, target, line);
            return;
          }
          root.MinimogEvents.emit(root.MinimogTheme.pubSubEvents.cartUpdate, { cart: response });
        })
        .catch(function (e) { try { console.log(e); } catch (x) {} })
        .finally(function () { try { if (self.loading) self.loading.finish(); } catch (e) {} });
    };
    C.prototype.__sdKeys = true;
  }

  doc.addEventListener('click', function (e) {
    var t = e.target && e.target.closest ? e.target : null;
    if (!t) return;
    var b;
    if ((b = t.closest('[data-sd-add]'))) { e.preventDefault(); addItem(b); return; }
    if ((b = t.closest('[data-sd-gift-remove]'))) { e.preventDefault(); removeGift(b); return; }
    if ((b = t.closest('[data-sd-addback]'))) { e.preventDefault(); addBack(b); return; }
    if ((b = t.closest('[data-sd-size]'))) { e.preventDefault(); pickSize(b); return; }
  });

  root.addEventListener('click', function (e) {
    var t = e.target && e.target.closest ? e.target.closest('button[name="checkout"]') : null;
    if (!t || t.getAttribute('data-sd-pass') === '1') return;
    if (!pending() && !S.holding) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    if (S.holding) return;
    S.holding = true;
    var host = t.closest('#MinimogCartDrawerFooter, #MinimogCartFooter');
    var sel = (host && host.id ? '#' + host.id + ' ' : '') + 'button[name="checkout"]';
    t.classList.add('sd-wait');
    settle(Date.now() + 3000).then(function () {
      S.holding = false;
      t.classList.remove('sd-wait');
      var live = t.isConnected ? t : doc.querySelector(sel) || doc.querySelector('button[name="checkout"]');
      if (!live) return;
      live.setAttribute('data-sd-pass', '1');
      live.click();
      setTimeout(function () { live.removeAttribute('data-sd-pass'); }, 0);
    });
  }, true);

  doc.addEventListener('cart:updated', function (e) {
    var c = e && e.detail && e.detail.cart;
    var mine = !!(c && c.sdLoot);
    if (!mine) S.acted = true;
    afterPaint();
    if (mine) return;
    schedule();
  });
  doc.addEventListener('cart:refresh', function () { S.acted = true; schedule(800); });
  doc.addEventListener('product-ajax:added', function () { S.acted = true; schedule(); });
  root.addEventListener('pageshow', function (e) { if (e.persisted) schedule(0); });

  function init() {
    patchLineKeys();
    if (root.customElements && root.customElements.whenDefined) {
      root.customElements.whenDefined('m-cart-items').then(patchLineKeys).catch(function () {});
    }
    resetOnVersion();
    afterPaint();
    var c = cfg();
    if (!c) return;
    if (Number(c.gift_lines) > 0 || Number(c.item_count) > 0) schedule(250);
    try {
      if (root.MinimogEvents && root.MinimogTheme && root.MinimogTheme.pubSubEvents) {
        root.MinimogEvents.subscribe(root.MinimogTheme.pubSubEvents.openCartDrawer, function () { afterPaint(); schedule(); });
      }
    } catch (e) {}
  }

  root.SDLoot = { sync: sync, schedule: schedule, pending: pending, core: Core };
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', init); else init();
})(typeof window !== 'undefined' ? window : globalThis);
